import { UnistylesRegistry } from 'react-native-unistyles';

export const breakpoints = {
    xs: 0,
    md: 768,
    lg: 1224,
} as const;

// Light (Classic design by Jorge)
export const lightTheme = {
    colors: {
        container: {
            background: '#FFFFFF',
            border: '#E6E6E6',
        },
        chat: {
            bubble: {
                background: '#d1ebfd',
                sentinelBackground: '#FFFFFF'
            },
            userAvatar: '#667798',
            sentinelAvatar: '#000028',
            background: '#FBFEFE',
            iconColor: '#000028',
            badgeBackground: '#FFFFFF',
            badgeTextColor: '#535353'
        },
        overlay: '#00000080',
        tabs: {
            border: '#888888',
            selected: '#000028',
        },
        sidebar: {
            background: '#000028',
            item: {
                hoverBackground: '#040745',
                selectedBackground: '#FFFFFF',
                text: '#FFFFFF',
                textSelected: '#000028',
            }
        },
        primaryButton: {
            background: '#000028',
            text: '#FFFFFF',

            hoverBackground: '#040745',
            hoverText: '#FFFFFF',

            disabledBackground: '#CCCCCC',
            disabledText: '#888888',
        },
        secondaryButton: {
            background: '#FFFFFF',
            borderColor: '#CCCCCC',
            text: '#222222',

            hoverBackground: '#E5E5E5',
            hoverBorderColor: '#CCCCCC',
            hoverText: '#181A1B',

            disabledBackground: '#FFFFFF',
            disabledBorderColor: '#CCCCCC',
            disabledText: '#222222',

            activeBackgroundColor: `#E5E5E5`,
        },
        defaultPageContainer: {
            background: '#F7F7F7'
        },
        checkbox: {
            unchecked: {
                border: '#CCCCCC',
                background: '#FFFFFF',
                icon: '#FFFFFF',

                hoverBorder: '#222222',
                hoverBackground: '#222222',
            },
            checked: {
                border: '#000028',
                background: '#000028',
                icon: '#FFFFFF',

                hoverBorder: '#040745',
                hoverBackground: '#040745',
            }
        },
        destructiveButton: {
            background: '#EB5252',
            text: '#FFFFFF',

            hoverBackground: '#FF8080',
            hoverText: '#FFFFFF',

            disabledBackground: '#CCCCCC',
            disabledText: '#888888',
        },
        paginationBar: {
            background: '#F9F9F9',
            border: '#E6E6E6',
            arrowButton: {
                border: '#CCCCCC',
                hoverBackground: '#CCCCCC',
                icon: '#222222',
                disabledIcon: '#CCCCCC',
            }
        },
        calendar: {
            background: '#ffffff',
            border: '#E6E6E6',
            navigationText: '#000',
            navigationDisabledText: '#CBCBCB',
            navigationBackgroundHover: '#ffffff14',
            dayText: '#222',
            dayBackgroundSelectedActive: '#000028',
            dayBackgroundSelectedRange: '#00002888',
            dayTextSelectedRangeColor: '#fff',
            dayTextDisabledColor: '#CBCBCB',
        },
        dataTable: {
            background: '#FFFFFF',
            border: '#E6E6E6',
            oddRowBackground: '#FAFAFA',
            rowOnHoverBackground: '#FAFAFA',
            oddRowOnHoverBackground: '#F5F5F5',
            actionIcon: '#58595B',
            loading: '#000000',
            noDataMessage: '#58595B',
            rowText: '#717171',
            alert: '#DE7133',
        },
        marker: {
            text: '#000000',
        },
        field: {
            border: '#CCCCCC',
            text: '#000000',
            background: '#FFFFFF',
            disabledBackground: '#EEEEEE'
        },
        online: {
            fontColor: '#1D6228',
            backgroundColor: '#ECFBED'
        },
        offline: {
            fontColor: '#790000',
            backgroundColor: '#FAE6E6'
        },
        card: {
            border: '#EEEEEE',
            background: '#FFFFFF',
            detailText: '#888888',
        },
        modal: {
            header: '#535353',
            subheader: '#717171',
        },
        switch: {
            background: '#FFFFFF',
            selectedTrack: '#835E00',
            unselectedTrack: '#CCCCCC',
        },
        primary: {
            '200': '#A5B1C2',
            '500': '#000028',
            'semi-transparent': '#00002864',
        },
        secondary: {
            '400': '#3C3C3D',
        },
        neutral: {
            '100': '#EFEFEF',
            '200': '#E2E2E2',
            '300': '#CCCCCC',
            '400': '#717171',
            '500': '#535353',
        },
        backgrounds: {
            'BG-1': '#F3F3F3',
            'BG-2': '#F9F9F9',
            'BG-4': '#FFEBC1',
            'BG-5': '#F9E9E9',
        },
        errors: {
            '500': '#790000',
            '300': '#EB5252',
            '200': '#BD5853',
            '150': '#FF8080'
        },
        successes: {
            '500': '#1D6228',
            '300': '#609168',
            '150': '#91DA9D'
        },
        alert: {
            '300': '#DE7133',
        },
        warnings: {
            '500': '#D64D00',
            '300': '#DE7133',
            '150': '#FDB389',
        },
        white: {
            '500': '#FFFFFF'
        },
        responsible: [
            '#919191',
            '#996679',
            '#996692',
            '#856699',
            '#996666',

            '#997266',
            '#998B66',
            '#8D9966',
            '#669969',
            '#669199',

            '#667799',
            '#717171',
            '#6D998B',
            '#836283',
            '#AD6EA9',
        ],
        badges: {
            blue: {
                backgroundColor: '#C4D0E9',
                textColor: '#294278',
            },
            red: {
                backgroundColor: '#EDC9C7',
                textColor: '#60332E',
            },
            orange: {
                backgroundColor: '#F0D1AC',
                textColor: '#8B4900',
            },
            green: {
                backgroundColor: '#CCE1CF',
                textColor: '#24622E',
            },
            purple: {
                backgroundColor: '#D6C4E9',
                textColor: '#552D7F',
            },
            gray: {
                backgroundColor: '#C8C8C8',
                textColor: '#3D3D3D',
            },
            yellow: {
                backgroundColor: '#E2E0AB',
                textColor: '#504D12',
            },
            transparencyBlack: {
                backgroundColor: '#000000BF',
                textColor: '#FFFFFF',
            },
            teal: {
                backgroundColor: '#BBE1EB',
                textColor: '#23515D'
            }
        },
        durationStatus: {
            onTime: {
                text: '#228432',
            },
            onGoing: {
                text: '#4E6FB4',
            },
            warning: {
                text: '#9A662B',
            },
            critical: {
                text: '#B25555',
            }
        },
        hoverText: '#222222',
        topMenu: '#FFFFFF',
        color: '#FFFFFF',
        disabledText: '#888888',
        loadingColor: '#000000',
        iconColor: '#58595B',
        labelColor: '#58595B',
        placeholder: '#CBCBCB',
        textColor: '#222222',
        red: '#D50000',
        green: '#008E2C',
        lightGreen: '#639f76',
        emphasisBlue: '#006683',
    },
} as const;

// Light (designed by Eduardo)
export const lightAltTheme = {
    colors: {
        container: {
            background: '#FFFFFF',
            border: '#E6E6E6',
        },
        chat: {
            bubble: {
                background: '#FDF6E5',
                sentinelBackground: '#FFFFFF'
            },
            userAvatar: '#667798',
            sentinelAvatar: '#000028',
            background: '#FEFDFB',
            iconColor: '#835E00',
            badgeBackground: '#FFFFFF',
            badgeTextColor: '#535353'
        },
        overlay: '#00000080',
        tabs: {
            border: '#888888',
            selected: '#835E00',
        },
        sidebar: {
            background: '#232426',
            item: {
                hoverBackground: '#3C3C3D',
                selectedBackground: '#3C3C3D',
                text: '#FFFFFF',
                textSelected: '#FFFFFF',
            }
        },
        primaryButton: {
            background: '#835E00',
            text: '#FFFFFF',

            hoverBackground: '#986D00',
            hoverText: '#FFFFFF',

            disabledBackground: '#D7C79F',
            disabledText: '#FFFFFF',
        },
        secondaryButton: {
            background: '#FFFFFF',
            borderColor: '#835E00',
            text: '#835E00',

            hoverBackground: '#FFFFFF',
            hoverBorderColor: '#986D00',
            hoverText: '#986D00',

            disabledBackground: '#FFFFFF',
            disabledBorderColor: '#D7C79F',
            disabledText: '#D7C79F',

            activeBackgroundColor: `#FDF6E5`,
        },
        defaultPageContainer: {
            background: '#F7F7F7'
        },
        checkbox: {
            unchecked: {
                border: '#CCCCCC',
                background: '#FFFFFF',
                icon: '#FFFFFF',

                hoverBorder: '#986D00',
                hoverBackground: '#986D00',
            },
            checked: {
                border: '#835E00',
                background: '#835E00',
                icon: '#FFFFFF',

                hoverBorder: '#986D00',
                hoverBackground: '#986D00',
            }
        },
        destructiveButton: {
            background: '#EB5252',
            text: '#FFFFFF',

            hoverBackground: '#FF8080',
            hoverText: '#FFFFFF',

            disabledBackground: '#CCCCCC',
            disabledText: '#888888',
        },
        paginationBar: {
            background: '#F9F9F9',
            border: '#E6E6E6',
            arrowButton: {
                border: '#CCCCCC',
                hoverBackground: '#CCCCCC',
                icon: '#222222',
                disabledIcon: '#CCCCCC',
            }
        },
        calendar: {
            background: '#ffffff',
            border: '#E6E6E6',
            navigationText: '#000',
            navigationDisabledText: '#CBCBCB',
            navigationBackgroundHover: '#ffffff14',
            dayText: '#222',
            dayBackgroundSelectedActive: '#835E00',
            dayBackgroundSelectedRange: '#835E0088',
            dayTextSelectedRangeColor: '#fff',
            dayTextDisabledColor: '#CBCBCB',
        },
        dataTable: {
            background: '#FFFFFF',
            border: '#E6E6E6',
            oddRowBackground: '#FAFAFA',
            rowOnHoverBackground: '#FAFAFA',
            oddRowOnHoverBackground: '#F5F5F5',
            actionIcon: '#58595B',
            loading: '#000000',
            noDataMessage: '#58595B',
            rowText: '#717171',
            alert: '#DE7133',
        },
        marker: {
            text: '#000000',
        },
        field: {
            border: '#CCCCCC',
            text: '#000000',
            background: '#FFFFFF',
            disabledBackground: '#EEEEEE'
        },
        online: {
            fontColor: '#1D6228',
            backgroundColor: '#ECFBED'
        },
        offline: {
            fontColor: '#790000',
            backgroundColor: '#FAE6E6'
        },
        card: {
            border: '#EEEEEE',
            background: '#FFFFFF',
            detailText: '#888888',
        },
        modal: {
            header: '#535353',
            subheader: '#717171',
        },
        switch: {
            background: '#FFFFFF',
            selectedTrack: '#835E00',
            unselectedTrack: '#CCCCCC',
        },
        primary: {
            '200': '#ECDBAF',
            '500': '#835E00',
            'semi-transparent': '#835E0064',
        },
        secondary: {
            '400': '#3C3C3D',
        },
        neutral: {
            '100': '#EFEFEF',
            '200': '#E2E2E2',
            '300': '#CCCCCC',
            '400': '#717171',
            '500': '#535353',
        },
        backgrounds: {
            'BG-1': '#F3F3F3',
            'BG-2': '#F9F9F9',
            'BG-4': '#FFEBC1',
            'BG-5': '#F9E9E9',
        },
        errors: {
            '500': '#790000',
            '300': '#EB5252',
            '200': '#BD5853',
            '150': '#FF8080'
        },
        successes: {
            '500': '#1D6228',
            '300': '#609168',
            '150': '#91DA9D'
        },
        alert: {
            '300': '#DE7133',
        },
        warnings: {
            '500': '#D64D00',
            '300': '#DE7133',
            '150': '#FDB389',
        },
        white: {
            '500': '#FFFFFF'
        },
        responsible: [
            '#919191',
            '#996679',
            '#996692',
            '#856699',
            '#996666',

            '#997266',
            '#998B66',
            '#8D9966',
            '#669969',
            '#669199',

            '#667799',
            '#717171',
            '#6D998B',
            '#836283',
            '#AD6EA9',
        ],
        badges: {
            blue: {
                backgroundColor: '#C4D0E9',
                textColor: '#294278',
            },
            red: {
                backgroundColor: '#EDC9C7',
                textColor: '#60332E',
            },
            orange: {
                backgroundColor: '#F0D1AC',
                textColor: '#8B4900',
            },
            green: {
                backgroundColor: '#CCE1CF',
                textColor: '#24622E',
            },
            purple: {
                backgroundColor: '#D6C4E9',
                textColor: '#552D7F',
            },
            gray: {
                backgroundColor: '#C8C8C8',
                textColor: '#3D3D3D',
            },
            yellow: {
                backgroundColor: '#E2E0AB',
                textColor: '#504D12',
            },
            transparencyBlack: {
                backgroundColor: '#000000BF',
                textColor: '#FFFFFF',
            },
            teal: {
                backgroundColor: '#BBE1EB',
                textColor: '#23515D'
            }
        },
        durationStatus: {
            onTime: {
                text: '#228432',
            },
            onGoing: {
                text: '#4E6FB4',
            },
            warning: {
                text: '#9A662B',
            },
            critical: {
                text: '#B25555',
            }
        },
        hoverText: '#222222',
        topMenu: '#FFFFFF',
        borderColor: '#E6E6E6',
        color: '#FFFFFF',
        disabledText: '#888888',
        loadingColor: '#000000',
        iconColor: '#58595B',
        labelColor: '#58595B',
        placeholder: '#CBCBCB',
        textColor: '#222222',
        red: '#D50000',
        green: '#008E2C',
        lightGreen: '#639f76',
        emphasisBlue: '#006683',
    },
} as const;

// Dark
export const darkTheme = {
    colors: {
        container: {
            background: '#181A1B',
            border: '#373C3E',
        },
        chat: {
            bubble: {
                background: '#3F4447',
                sentinelBackground: '#404040'
            },
            userAvatar: '#82ACFF',
            sentinelAvatar: '#FFFFFF',
            background: '#171717',
            iconColor: '#FFFFFF',
            badgeBackground: '#404040',
            badgeTextColor: '#FFFFFF'
        },
        overlay: '#00000080',
        tabs: {
            border: '#888888',
            selected: '#FFFFFF',
        },
        sidebar: {
            background: '#232426',
            item: {
                hoverBackground: '#3C3C3D',
                selectedBackground: '#FFFFFF',
                text: '#FFFFFF',
                textSelected: '#232426',
            }
        },
        primaryButton: {
            background: '#FFFFFF',
            text: '#181A1B',

            hoverBackground: '#E5E5E5',
            hoverText: '#181A1B',

            disabledBackground: '#3F4447',
            disabledText: '#888888',
        },
        secondaryButton: {
            background: '#181A1B',
            borderColor: '#3F4447',
            text: '#D3CFC9',

            hoverBackground: '#26292B',
            hoverBorderColor: '#3F4447',
            hoverText: '#D3CFC9',

            disabledBackground: '#181A1B',
            disabledBorderColor: '#3F4447',
            disabledText: '#D3CFC9',

            activeBackgroundColor: `#3F4447`,
        },
        defaultPageContainer: {
            background: '#1D1F20',
        },
        checkbox: {
            unchecked: {
                border: '#3F4447',
                background: '#181A1B',
                icon: '#FFFFFF',

                hoverBorder: '#CCCCCC',
                hoverBackground: '#CCCCCC',
            },
            checked: {
                border: '#FFFFFF',
                background: '#FFFFFF',
                icon: '#181A1B',

                hoverBorder: '#E5E5E5',
                hoverBackground: '#E5E5E5',
            }
        },
        destructiveButton: {
            background: '#EB5252',
            text: '#FFFFFF',

            hoverBackground: '#FF8080',
            hoverText: '#FFFFFF',

            disabledBackground: '#CCCCCC',
            disabledText: '#888888',
        },
        paginationBar: {
            background: '#1D1F20',
            border: '#373C3E',
            arrowButton: {
                border: '#3F4447',
                hoverBackground: '#3F4447',
                icon: '#D3CFC9',
                disabledIcon: '#3F4447',
            }
        },
        calendar: {
            background: '#1e1e1e',
            border: '#333',
            dayText: '#eee',
            navigationText: '#fff',
            navigationDisabledText: '#777',
            navigationBackgroundHover: '#ffffff14',
            dayBackgroundSelectedActive: '#4f46e5',
            dayBackgroundSelectedRange: '#4f46e54d',
            dayTextSelectedRangeColor: '#fff',
            dayTextDisabledColor: '#666',
        },
        dataTable: {
            background: '#181A1B',
            border: '#373C3E',
            oddRowBackground: '#1D1F20',
            rowOnHoverBackground: '#1D1F20',
            oddRowOnHoverBackground: '#222425',
            actionIcon: '#D3CFC9',
            loading: '#FFFFFF',
            noDataMessage: '#D3CFC9',
            rowText: '#CCCCCC',
            alert: '#FDB389',
        },
        marker: {
            text: '#FFFFFF',
        },
        field: {
            border: '#3F4447',
            text: '#FFFFFF',
            background: '#181A1B',
            disabledBackground: '#2A2A2A'
        },
        online: {
            fontColor: '#91DA9D',
            backgroundColor: '#1F4022'
        },
        offline: {
            fontColor: '#FF8080',
            backgroundColor: '#FAE6E6'
        },
        card: {
            border: '#2A2A2A',
            background: '#181A1B',
            detailText: '#888888',
        },
        modal: {
            header: '#FFFFFF',
            subheader: '#717171',
        },
        switch: {
            background: '#FFFFFF',
            selectedTrack: '#CF9500',
            unselectedTrack: '#CCCCCC',
        },
        primary: {
            '200': '#878787',
            '500': '#FFFFFF',
            'semi-transparent': '#FFFFFF64',
        },
        secondary: {
            '400': '#D3CFC9',
        },
        neutral: {
            '100': '#333333',
            '200': '#4C4C4C',
            '300': '#666666',
            '400': '#808080',
            '500': '#999999',
        },
        backgrounds: {
            'BG-1': '#2E2E2E',
            'BG-2': '#121212',
            'BG-4': '#332B1A',
            'BG-5': '#331A1A',
        },
        errors: {
            '500': '#790000',
            '300': '#EB5252',
            '200': '#BD5853',
            '150': '#FF8080'
        },
        successes: {
            '500': '#1D6228',
            '300': '#609168',
            '150': '#91DA9D'
        },
        alert: {
            '300': '#DE7133',
        },
        warnings: {
            '500': '#D64D00',
            '300': '#DE7133',
            '150': '#FDB389',
        },
        white: {
            '500': '#181A1B'
        },
        responsible: [
            '#747474',
            '#7A5261',
            '#7A5275',
            '#6A527A',
            '#7A5252',

            '#7A5B52',
            '#7A6F52',
            '#717A52',
            '#527A54',
            '#52747A',

            '#525F7A',
            '#5A5A5A',
            '#577A6F',
            '#694E69',
            '#8A5887',
        ],
        badges: {
            blue: {
                backgroundColor: '#294278',
                textColor: '#C4D0E9',
            },
            red: {
                backgroundColor: '#60332E',
                textColor: '#EDC9C7',
            },
            orange: {
                backgroundColor: '#8B4900',
                textColor: '#F0D1AC',
            },
            green: {
                backgroundColor: '#24622E',
                textColor: '#CCE1CF',
            },
            purple: {
                backgroundColor: '#552D7F',
                textColor: '#D6C4E9',
            },
            gray: {
                backgroundColor: '#3D3D3D',
                textColor: '#C8C8C8',
            },
            yellow: {
                backgroundColor: '#504D12',
                textColor: '#E2E0AB',
            },
            transparencyBlack: {
                backgroundColor: '#000000BF',
                textColor: '#FFFFFF',
            },
            teal: {
                backgroundColor: '#23515D',
                textColor: '#BBE1EB'
            }
        },
        durationStatus: {
            onTime: {
                text: '#228432',
            },
            onGoing: {
                text: '#4E6FB4',
            },
            warning: {
                text: '#9A662B',
            },
            critical: {
                text: '#B25555',
            }
        },
        hoverText: '#222222',
        topMenu: '#181A1B',
        color: '#181A1B',
        disabledText: '#888888',
        loadingColor: '#FFFFFF',
        iconColor: '#D3CFC9',
        labelColor: '#D3CFC9',
        placeholder: '#3F4447',
        textColor: '#D3CFC9',
        red: '#D50000',
        green: '#00BA39',
        lightGreen: '#44975d',
        emphasisBlue: '#00B0E0',
    },
} as const;


type AppThemes = {
    light: typeof lightTheme;
    dark: typeof darkTheme;
    lightAlt: typeof lightAltTheme;
};

// if you defined breakpoints
type AppBreakpoints = typeof breakpoints;

// override library types
declare module 'react-native-unistyles' {
    export interface UnistylesBreakpoints extends AppBreakpoints { }

    export interface UnistylesThemes extends AppThemes { }
}

UnistylesRegistry
    .addBreakpoints(breakpoints)
    .addThemes({
        light: lightTheme,
        dark: darkTheme,
        lightAlt: lightAltTheme,
    });
